<template>
  <div>
    <div class="d-flex justify-content-between pb-3" style="gap: 15px">
      <div class="d-flex align-items-center" style="flex: 0 1 auto; width: 60%">
        <b-form-select
          v-model="selectedPeriod"
          @change="clearMultiselect"
          :options="periodOptions"
          size="md"
          class="mr-3 mb-0 w-25"
          :disabled="tableLoading"
        >
        </b-form-select>

        <div class="form-group mr-3 mb-0 mt-1" style="width: 80%">
          <MultiSelectInput v-model="multiselectValue" :options="multiselectOptions" :disabled="tableLoading">
          </MultiSelectInput>
        </div>

        <b-form-select
          v-model="selectedYear"
          :options="yearOptions"
          size="md"
          class="mb-0 w-25"
          :disabled="tableLoading"
        >
        </b-form-select>
      </div>

      <div class="d-flex align-items-center">
        <b-button variant="primary" size="sm" :disabled="isBtnDisabled" class="mr-3" @click="downloadReport('view')">
          Preview PDF
        </b-button>
        <b-button variant="primary" size="sm" :disabled="isBtnDisabled" @click="downloadReport('download')">
          Download PDF
        </b-button>
      </div>
    </div>
    <ReportTable
      :table-loading="tableLoading"
      :fields="fields"
      :static-fields="staticFields"
      :table-data="tableData"
      :is-no-data="isNoData"
    />
  </div>
</template>

<script>
import { numberFormatter, manageReportFiles } from '@/utils/helpers'
import { mapActions } from 'vuex'
import ReportTable from '@/components/Reports/ReportTable.vue'

const IncomeTableColumns = [
  {
    id: 1,
    key: 'id',
    label: 'ID',
    viewRecord: false,
  },
  {
    id: 2,
    key: 'account_name',
    label: 'Account Name',
    viewRecord: true,
  },
  {
    id: 3,
    key: 'account_number',
    label: 'Account Number',
    viewRecord: true,
  },
  // {
  //   id: 4,
  //   key: 'allowable',
  //   label: 'Allowable',
  //   viewRecord: true,
  // },
  {
    id: 5,
    key: 'focus_id',
    label: 'Focus ID',
    viewRecord: false,
  },
  {
    id: 6,
    key: 'focus_code',
    label: 'FOCUS',
    viewRecord: true,
  },
  {
    id: 7,
    key: 'ssoi_id',
    label: 'SSOI ID',
    viewRecord: false,
  },
  {
    id: 8,
    key: 'ssoi_code',
    label: 'SSOI',
    viewRecord: true,
  },
]

export default {
  name: 'IncomeStatement',
  components: {
    ReportTable,
  },
  data: () => ({
    fields: [],
    staticFields: ['account_name', 'account_number', 'focus_code', 'ssoi_code'],
    equityTotals: [],
    isNoData: false,
    status: '',
    // table action
    periodOptions: [
      {
        value: 'Month',
        text: 'Month',
      },
      {
        value: 'Quarter',
        text: 'Quarter',
      },
      {
        value: 'Year',
        text: 'Year',
      },
    ],
    selectedPeriod: 'Month',
    selectedYear: parseInt(new Date().getFullYear().toString().substr(-2)),
    multiselectValue: null,
    tableData: [],
  }),
  watch: {
    query: {
      handler() {
        if (this.query.length > 0) {
          this.getReport()
        }
      },
      immediate: true,
    },
  },
  created() {
    this.getAvailableTimeReports()
  },
  computed: {
    yearOptions() {
      let options = []
      for (let i = 0; i < 5; i++) {
        options.push({
          value: parseInt(new Date().getFullYear().toString().substr(-2)) - i,
          text: parseInt(new Date().getFullYear().toString()) - i,
        })
      }
      return options
    },
    multiselectOptions() {
      let options = []
      if (this.selectedPeriod === 'Month') {
        for (let i = 0; i < 12; i++) {
          options.push({
            value: new Date(new Date().getFullYear(), i, 1).toLocaleString('default', { month: 'short' }).toUpperCase(),
            text: new Date(new Date().getFullYear(), i, 1).toLocaleString('default', { month: 'short' }).toUpperCase(),
          })
        }
      } else if (this.selectedPeriod === 'Quarter') {
        for (let i = 0; i < 4; i++) {
          options.push({
            value: `Q${i + 1}`,
            text: `Q${i + 1}`,
          })
        }
      } else if (this.selectedPeriod === 'Year') {
        options.push(
          {
            value: '1st HALF',
            text: '1st HALF',
          },
          {
            value: '2nd HALF',
            text: '2nd HALF',
          },
        )
      } else {
        options = []
      }
      return options
    },
    query() {
      let data = []
      this.multiselectValue?.forEach((item) => {
        if (item.value === '1st HALF') {
          for (let i = 1; i <= 6; i++) {
            data.push(
              `${new Date(this.selectedYear, i, 0).toLocaleString('default', { month: 'short' })} ${this.selectedYear}`,
            )
          }
          data.push(`Q1 ${this.selectedYear}`)
          data.push(`Q2 ${this.selectedYear}`)
        } else if (item.value === '2nd HALF') {
          for (let i = 6; i <= 12; i++) {
            data.push(
              `${new Date(this.selectedYear, i, 0).toLocaleString('default', { month: 'short' })} ${this.selectedYear}`,
            )
          }
          data.push(`Q3 ${this.selectedYear}`)
          data.push(`Q4 ${this.selectedYear}`)
        } else {
          data.push(`${item.value} ${this.selectedYear}`)
        }
      })
      return data
    },
    tableLoading() {
      return this.status === 'loading'
    },
    isBtnDisabled() {
      return this.tableData.length === 0
    },
  },
  methods: {
    ...mapActions({
      setLoading: 'loader/setLoading',
      setPdf: 'report/setPdf',
    }),
    async getReport() {
      this.status = 'loading'
      this.tableData = []
      try {
        const response = await this.$api.get('/dashboard/income-sheet', {
          params: {
            time_reports: this.query,
            company_id: this.company.id,
          },
        })

        const incomeRecords = this.filterSortRecord(response.data?.data?.Income?.data) || []
        const expenseRecords = this.filterSortRecord(response.data?.data?.Expenses?.data) || []
        const netIncomeRecords = this.filterSortRecord(response.data?.data?.['Net Income']?.data) || []

        this.fields = IncomeTableColumns.filter((_) => _.viewRecord)

        if (incomeRecords.length === 0 && expenseRecords.length === 0 && netIncomeRecords.length === 0) {
          this.status = 'error'
          this.isNoData = true
          return
        }

        this.query.forEach((item) => {
          this.fields.push({
            key: item,
            label: `${item.split('_')[0].toUpperCase()}`,
            sortable: true,
            viewRecord: true,
          })
        })

        this.fields.push({
          key: 'total',
          label: 'Total',
          sortable: true,
          viewRecord: true,
        })

        if (incomeRecords.length > 0) {
          const { name, ...rest } = response.data?.data?.Income?.total || {}
          this.tableData.push({
            type: 'Income',
            records: incomeRecords,
            totals: {
              name,
              totalsByMonth: Object.values(rest).map((value) => numberFormatter(value)),
            },
          })
        }
        if (expenseRecords.length > 0) {
          const { name, ...rest } = response.data?.data?.Expenses?.total || {}
          this.tableData.push({
            type: 'Expenses',
            records: expenseRecords,
            totals: {
              name,
              totalsByMonth: Object.values(rest).map((value) => numberFormatter(value)),
            },
          })
        }
        if (netIncomeRecords.length > 0 || response.data?.data?.['Net Income']?.total !== undefined) {
          const { name, ...rest } = response.data?.data?.['Net Income']?.total || {}
          this.tableData.push({
            type: 'Net Income',
            records: netIncomeRecords,
            totals: {
              name,
              totalsByMonth: Object.values(rest).map((value) => numberFormatter(value)),
            },
          })
        }
        this.status = 'success'
      } catch (error) {
        this.status = 'error'
        this.$showError(error.message || 'Unable to fetch api records')
      }
    },
    filterSortRecord(records) {
      const columns = ['account_name', 'account_number', 'focus_code', 'ssoi_code']
      this.query.forEach((item) => {
        columns.push(item.split(' ').join('_').toLowerCase())
      })

      columns.push('total')

      return records?.map((obj) => {
        return columns.reduce((acc, key) => {
          if (Object.prototype.hasOwnProperty.call(obj, key)) {
            if (key !== 'account_name' && key !== 'account_number' && key !== 'focus_code' && key !== 'ssoi_code') {
              acc[key] = numberFormatter(obj[key])
            } else {
              acc[key] = obj[key]
            }
          }
          return acc
        }, {})
      })
    },
    clearMultiselect() {
      this.multiselectValue = []
    },
    downloadReport(mode) {
      this.setLoading(true)
      const params = {
        params: {
          time_reports: this.query,
          download: true,
        },
      }
      this.$api
        .get(`/dashboard/income-sheet?company_id=${this.company.id}`, params)
        .then((res) => {
          if (!res.data?.data) {
            this.$showInfo('There is no report.')
            return
          }
          const reportName = this.query.join('_').toUpperCase()
          const createdPDF = manageReportFiles(res.data)
          if (mode === 'download') {
            createdPDF.download(`Income_Statement_${reportName}.pdf`)
            return
          }
          createdPDF.getDataUrl((dataUrl) => {
            this.setPdf({
              src: dataUrl,
              title: `Income_Statement_${reportName}`,
            })
            this.$bvModal.show('PdfModal')
          })
        })
        .catch((err) => {
          this.$showError(err.response?.data?.error?.message || 'Unable to fetch api records')
        })
        .finally(() => {
          this.setLoading(false)
        })
    },
    async getAvailableTimeReports() {
      this.status = 'loading'
      try {
        const response = await this.$api.get(`/trial-balances/available-time-report?company_id=${this.company.id}`)
        console.log(response)
        if (response.status === 200 && response.data?.data?.length > 0) {
          this.multiselectValue = [
            {
              value: response.data.data[0].time_report.split(' ')[0],
              text: response.data.data[0].time_report.split(' ')[0],
            },
          ]
          this.selectedYear = parseInt(response.data.data[0].time_report.split(' ')[1])
        } else {
          this.multiselectValue = [
            {
              value: new Date().toLocaleString('default', { month: 'short' }).toUpperCase(),
              text: new Date().toLocaleString('default', { month: 'short' }).toUpperCase(),
            },
          ]
        }
        this.status = 'success'
      } catch (error) {
        this.status = 'error'
        this.$showError(error.message || 'Unable to fetch api records')
      }
    },
  },
}
</script>
